import { useState } from 'react';
import { Shield, AlertTriangle, Ban, Loader2, RefreshCw, Trash2, Plus } from 'lucide-react';
import { useSecurityEvents, useBlockedIps, useBlockIp, useUnblockIp } from '@/hooks/use-security';
import { useMyCapabilities } from '@/hooks/use-permissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/toaster';
import { formatDistanceToNow } from 'date-fns';
import { formatDisplayDateTime } from '@/lib/date-format';

export function SecurityPage() {
  const { data: capabilities } = useMyCapabilities();
  const canManage = Boolean(capabilities?.['security.manage']);

  const { data: eventsData, isLoading: eventsLoading, refetch: refetchEvents } = useSecurityEvents({
    limit: 50,
  });
  const { data: blockedIps, isLoading: blockedLoading, refetch: refetchBlocked } = useBlockedIps();

  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [newIp, setNewIp] = useState('');
  const [newReason, setNewReason] = useState('');
  const [blockDuration, setBlockDuration] = useState('24');

  const blockIp = useBlockIp();
  const unblockIp = useUnblockIp();

  const handleBlockIp = async () => {
    if (!newIp.trim() || !newReason.trim()) {
      toast({ title: 'Please fill in all fields', variant: 'destructive' });
      return;
    }

    try {
      const blockedUntil = blockDuration
        ? new Date(Date.now() + parseInt(blockDuration) * 60 * 60 * 1000).toISOString()
        : undefined;

      await blockIp.mutateAsync({
        ipAddress: newIp.trim(),
        reason: newReason.trim(),
        blockedUntil,
      });

      toast({ title: 'IP blocked successfully' });
      setBlockDialogOpen(false);
      setNewIp('');
      setNewReason('');
      setBlockDuration('24');
    } catch {
      toast({ title: 'Failed to block IP', variant: 'destructive' });
    }
  };

  const handleUnblockIp = async (id: string) => {
    try {
      await unblockIp.mutateAsync(id);
      toast({ title: 'IP unblocked successfully' });
    } catch {
      toast({ title: 'Failed to unblock IP', variant: 'destructive' });
    }
  };

  const handleRefresh = () => {
    refetchEvents();
    refetchBlocked();
  };

  if (eventsLoading || blockedLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Security Center
          </h1>
          <p className="text-muted-foreground">
            Monitor security events and manage blocked IPs
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5" />
              Blocked IPs
            </CardTitle>
            <CardDescription>
              {blockedIps?.length || 0} IP addresses currently blocked
            </CardDescription>
          </div>
          {canManage && (
            <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Block IP
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Block IP Address</DialogTitle>
                  <DialogDescription>
                    Manually block an IP address from accessing the system.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="ip">IP Address</Label>
                    <Input
                      id="ip"
                      placeholder="192.168.1.1"
                      value={newIp}
                      onChange={(e) => setNewIp(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reason">Reason</Label>
                    <Input
                      id="reason"
                      placeholder="Suspicious activity"
                      value={newReason}
                      onChange={(e) => setNewReason(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="duration">Block Duration (hours)</Label>
                    <Input
                      id="duration"
                      type="number"
                      placeholder="24"
                      value={blockDuration}
                      onChange={(e) => setBlockDuration(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Leave empty for permanent block
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setBlockDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleBlockIp} disabled={blockIp.isPending}>
                    {blockIp.isPending ? 'Blocking...' : 'Block IP'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent>
          {blockedIps && blockedIps.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Blocked Until</TableHead>
                  <TableHead>Created</TableHead>
                  {canManage && <TableHead className="w-[80px]">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {blockedIps.map((ip) => (
                  <TableRow key={ip.id}>
                    <TableCell className="font-mono text-sm">{ip.ipAddress}</TableCell>
                    <TableCell>{ip.reason}</TableCell>
                    <TableCell>
                      {ip.blockedUntil ? (
                        <span className="text-sm">
                          {formatDisplayDateTime(ip.blockedUntil)}
                        </span>
                      ) : (
                        <Badge variant="destructive">Permanent</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDistanceToNow(new Date(ip.createdAt), { addSuffix: true })}
                    </TableCell>
                    {canManage && (
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleUnblockIp(ip.id)}
                          disabled={unblockIp.isPending}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No blocked IPs
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Recent Security Events
          </CardTitle>
          <CardDescription>
            {eventsData?.total || 0} total events recorded
          </CardDescription>
        </CardHeader>
        <CardContent>
          {eventsData?.events && eventsData.events.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Path</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="hidden lg:table-cell">User Agent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {eventsData.events.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                      {formatDistanceToNow(new Date(event.createdAt), { addSuffix: true })}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{event.ipAddress}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{event.method}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm max-w-[200px] truncate" title={event.path}>
                      {event.path}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={event.statusCode === 403 ? 'destructive' : 'secondary'}
                      >
                        {event.statusCode}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{event.reason}</Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-xs text-muted-foreground max-w-[200px] truncate" title={event.userAgent || ''}>
                      {event.userAgent || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No security events recorded
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
